import React, { useState } from 'react'
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Card,
  CardContent,
  Alert,
  CircularProgress
} from '@mui/material'
import {
  School as SchoolIcon,
  Person as PersonIcon
} from '@mui/icons-material'
import { doc, updateDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore'
import { auth, db } from '../firebase/config'
import { useAuth } from '../hooks/useAuth'
import toast from 'react-hot-toast'

const RoleSelection = ({ onComplete }) => {
  const { user } = useAuth()
  const [role, setRole] = useState('student')
  const [username, setUsername] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleRoleChange = (event) => {
    setRole(event.target.value)
  }

  const validateUsername = (username) => {
    if (username.trim().length === 0) {
      return 'Lütfen kullanıcı adınızı girin'
    }
    if (username.trim().length < 3) {
      return 'Kullanıcı adı en az 3 karakter olmalıdır'
    }
    if (username.trim().length > 20) {
      return 'Kullanıcı adı en fazla 20 karakter olabilir'
    }
    return null
  }

  const checkUsernameAvailability = async (username) => {
    try {
      const q = query(collection(db, 'users'), where('username', '==', username))
      const querySnapshot = await getDocs(q)
      return querySnapshot.empty
    } catch (error) {
      console.error('Username check error:', error)
      throw new Error('Kullanıcı adı kontrolü sırasında hata oluştu')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    const validationError = validateUsername(username)
    if (validationError) {
      setError(validationError)
      toast.error(validationError)
      return
    }

    setLoading(true)
    setError('')

    try {
      // Username kullanılabilirliğini kontrol et
      const isAvailable = await checkUsernameAvailability(username)
      if (!isAvailable) {
        setError('Bu kullanıcı adı zaten kullanılıyor')
        toast.error('Bu kullanıcı adı zaten kullanılmaktadır!')
        return
      }

      // Kullanıcı bilgilerini güncelle
      const userData = {
        username: username.trim(),
        email: user.email,
        level: 'A1',
        signInMethod: 'google',
        isAdmin: false,
        role: role,
        profileImageUrl: user.photoURL || '',
        createdAt: new Date(),
        lastLogin: new Date()
      }

      // Firestore'a kaydet
      await setDoc(doc(db, 'users', user.uid), userData, { merge: true })

      toast.success('Profil bilgileriniz başarıyla kaydedildi!')
      
      // Rol seçimi tamamlandı, ana sayfaya yönlendir
      onComplete(userData)
      
    } catch (error) {
      console.error('Profile update error:', error)
      setError('Profil güncellenirken hata oluştu')
      toast.error('Profil güncellenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography component="h1" variant="h4" gutterBottom>
            Rolünüzü Seçin
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            EngliX'te nasıl kullanmak istiyorsunuz?
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            {/* Kullanıcı Adı */}
            <TextField
              margin="normal"
              required
              fullWidth
              label="Kullanıcı Adı"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Kullanıcı adınızı girin"
              sx={{ mb: 3 }}
            />

            {/* Rol Seçimi */}
            <FormControl component="fieldset" sx={{ width: '100%', mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Rolünüzü Seçin
              </Typography>
              
              <RadioGroup
                value={role}
                onChange={handleRoleChange}
                sx={{ gap: 2 }}
              >
                {/* Öğrenci Seçeneği */}
                <Card 
                  variant="outlined" 
                  sx={{ 
                    cursor: 'pointer',
                    borderColor: role === 'student' ? 'primary.main' : 'divider',
                    backgroundColor: role === 'student' ? 'primary.50' : 'background.paper',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'primary.50'
                    }
                  }}
                  onClick={() => setRole('student')}
                >
                  <CardContent sx={{ py: 2 }}>
                    <FormControlLabel
                      value="student"
                      control={<Radio />}
                      label={
                        <Box display="flex" alignItems="center">
                          <SchoolIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Box>
                            <Typography variant="h6">Öğrenci</Typography>
                            <Typography variant="body2" color="text.secondary">
                              İngilizce öğrenmek istiyorum
                            </Typography>
                          </Box>
                        </Box>
                      }
                      sx={{ width: '100%', margin: 0 }}
                    />
                  </CardContent>
                </Card>

                {/* Öğretmen Seçeneği */}
                <Card 
                  variant="outlined" 
                  sx={{ 
                    cursor: 'pointer',
                    borderColor: role === 'teacher' ? 'primary.main' : 'divider',
                    backgroundColor: role === 'teacher' ? 'primary.50' : 'background.paper',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'primary.50'
                    }
                  }}
                  onClick={() => setRole('teacher')}
                >
                  <CardContent sx={{ py: 2 }}>
                    <FormControlLabel
                      value="teacher"
                      control={<Radio />}
                      label={
                        <Box display="flex" alignItems="center">
                          <PersonIcon sx={{ mr: 1, color: 'secondary.main' }} />
                          <Box>
                            <Typography variant="h6">Öğretmen</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Öğrencilerime ders vermek istiyorum
                            </Typography>
                          </Box>
                        </Box>
                      }
                      sx={{ width: '100%', margin: 0 }}
                    />
                  </CardContent>
                </Card>
              </RadioGroup>
            </FormControl>

            {/* Devam Butonu */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !username.trim()}
              sx={{ mt: 2 }}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                'Devam Et'
              )}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  )
}

export default RoleSelection
